import { Alert, Box, Chip, Grid, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../components/ui/Section';
import { demoRecipeV2Client } from '../../../app/services/api/recipeV2';
import { isDemoModeEnabled } from '../../../utils/featureAccess';

interface DemoRecipeIdentityProps {
  recipeId: number;
}

/** Thomas Franks presentation fields are a read-only snapshot, separate from Recipe V2 writes. */
const DemoRecipeIdentity = ({ recipeId }: DemoRecipeIdentityProps) => {
  const { t } = useTranslation('recipeV2');
  if (!isDemoModeEnabled()) return null;

  const demoRecipe = demoRecipeV2Client.getDemoIdentity(recipeId);
  if (!demoRecipe) return null;

  const controls = demoRecipeV2Client.getDemoAdvancedDetail(recipeId)?.criticalControls ?? [];
  const identity = [
    [t('demoIdentity.customerFacingName'), demoRecipe.customerFacingName],
    [t('demoIdentity.operationalName'), demoRecipe.operationalName],
    [t('demoIdentity.customerDescription'), demoRecipe.shortDescription],
    [t('demoIdentity.story'), demoRecipe.story],
  ].filter(([, value]) => Boolean(value));
  const service = [
    [t('demoIdentity.dietaryPosition'), demoRecipe.dietaryPosition],
    [t('demoIdentity.serviceMethod'), demoRecipe.serviceMethod],
  ].filter(([, value]) => Boolean(value));

  if (identity.length === 0 && service.length === 0 && !demoRecipe.suitableFor?.length && !controls.length) return null;

  return (
    <Section style={{ p: 4, mb: 0 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="textLG"
            fontWeight={600}>{t('demoIdentity.title')}</Typography>
          <Alert severity="info"
            sx={{ mt: 1 }}>
            {t('demoIdentity.readOnly')}
          </Alert>
        </Box>
        {identity.map(([label, value]) => (
          <Box key={label}>
            <Typography variant="textXS"
              color="text.secondary">{label}</Typography>
            <Typography variant="textSM">{value}</Typography>
          </Box>
        ))}
        {(service.length > 0 || Boolean(demoRecipe.suitableFor?.length)) && (
          <Grid container
            spacing={3}>
            {service.map(([label, value]) => (
              <Grid item
                xs={12}
                sm={6}
                key={label}>
                <Typography variant="textXS"
                  color="text.secondary">{label}</Typography>
                <Typography variant="textSM">{value}</Typography>
              </Grid>
            ))}
            {Boolean(demoRecipe.suitableFor?.length) && (
              <Grid item
                xs={12}>
                <Typography variant="textXS"
                  color="text.secondary">{t('demoIdentity.suitableFor')}</Typography>
                <Stack direction="row"
                  spacing={1}
                  flexWrap="wrap">
                  {demoRecipe.suitableFor?.map((use) => <Chip key={use}
                    label={use}
                    size="small" />)}
                </Stack>
              </Grid>
            )}
          </Grid>
        )}
        {controls.length > 0 && (
          <Box>
            <Typography variant="textSM"
              fontWeight={600}>{t('demoIdentity.criticalControls')}</Typography>
            <Box component="ul"
              sx={{ mt: 1, mb: 0, pl: 3 }}>
              {controls.map((control) => (
                <Typography component="li"
                  variant="textSM"
                  key={control}>{control}</Typography>
              ))}
            </Box>
          </Box>
        )}
      </Stack>
    </Section>
  );
};

export default DemoRecipeIdentity;
